import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import DrawerItems from "./DrawerItems";
import NavbarNavigationItems from "./NavbarNavigationItems";
import NavbarLogo from "./NavbarLogo";
import SvgIcon from "../../helpers/SvgIcon";
import useMediaQuery from "@mui/material/useMediaQuery";

const drawerWidth = 276;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  minWidth: 0,
  // padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  // justifyContent: "flex-start",
}));

export default function NavbarPersistWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [open, setOpen] = React.useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box className="flex h-full">
      <AppBar position="fixed" open={isDesktop && open} elevation={3}>
        <Toolbar>
          {(!open || !isDesktop) && (
            <IconButton
              color="inherit"
              aria-label="Deschide meniul"
              aria-expanded={open}
              onClick={handleDrawerOpen}
              edge="start">
              <SvgIcon type="MENU_ICON" fill="currentColor" />
            </IconButton>
          )}
          {(!open || !isDesktop) && <NavbarLogo withEnvIndicator />}
          <NavbarNavigationItems />
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: isDesktop ? drawerWidth : 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "1px solid #e8edf3",
            boxShadow: "4px 0 24px rgba(24, 28, 50, 0.06)",
          },
        }}
        variant={isDesktop ? "persistent" : "temporary"}
        anchor="left"
        onClose={handleDrawerClose}
        open={open}>
        <DrawerHeader className="shrink-0 border-b border-slate-100">
          <div className="flex w-full items-center justify-between">
            {open && <NavbarLogo />}

            <IconButton
              aria-label="Închide meniul"
              onClick={handleDrawerClose}
              className="!h-9 !w-9 !rounded-lg !text-muted-dark hover:!bg-surface-subtle">
              {theme.direction === "ltr" ? (
                <SvgIcon type="CHEVRON_LEFT_ICON" fill="currentColor" />
              ) : (
                <SvgIcon type="CHEVRON_RIGHT_ICON" fill="currentColor" />
              )}
            </IconButton>
          </div>
        </DrawerHeader>
        <DrawerItems handleDrawerClose={handleDrawerClose} />
      </Drawer>
      <Main open={!isDesktop || open}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
}
